import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext(null);

// Session configuration
const SESSION_CONFIG = {
  IDLE_TIMEOUT: 30 * 60 * 1000,      // 30 minutes idle timeout
  MAX_SESSION: 10 * 60 * 60 * 1000,  // 10 hours max session
  REMEMBER_ME_DAYS: 7,                // 7 days for "remember me"
  ACTIVITY_EVENTS: ['mousedown', 'keydown', 'touchstart', 'scroll']
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStart, setSessionStart] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if user is clocked in (check localStorage)
  const checkClockedInStatus = useCallback(() => {
    if (!user) return false;
    
    // Find employee by email and check their clock-in status
    const today = new Date().toISOString().split('T')[0];
    
    // We need to check all possible employee IDs since we don't have direct access
    // The TeamApp stores clock-in as: clockIn_${employeeId}_${date}
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('clockIn_') && key.endsWith(`_${today}`)) {
        const value = localStorage.getItem(key);
        if (value) {
          return true;
        }
      }
    }
    return false;
  }, [user]);

  // Force logout
  const forceLogout = useCallback(async (reason = 'session_expired') => {
    console.log(`Session ended: ${reason}`);
    
    // Clear timers
    if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    
    // Clear session data
    sessionStorage.removeItem('sessionStart');
    sessionStorage.removeItem('rememberMe');
    
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Force logout error:', error);
    }
  }, []);

  // Check idle timeout (only if not clocked in)
  const checkIdleTimeout = useCallback(() => {
    const clockedIn = checkClockedInStatus();
    setIsClockedIn(clockedIn);
    
    // Skip idle check if clocked in
    if (clockedIn) {
      return;
    }
    
    const idleTime = Date.now() - lastActivityRef.current;
    if (idleTime >= SESSION_CONFIG.IDLE_TIMEOUT) {
      forceLogout('idle_timeout');
    }
  }, [checkClockedInStatus, forceLogout]);

  // Check max session timeout
  const checkSessionTimeout = useCallback(() => {
    const start = sessionStorage.getItem('sessionStart');
    if (start) {
      const sessionDuration = Date.now() - parseInt(start, 10);
      if (sessionDuration >= SESSION_CONFIG.MAX_SESSION) {
        forceLogout('max_session_exceeded');
      }
    }
  }, [forceLogout]);

  // Setup activity listeners
  useEffect(() => {
    if (!user) return;

    // Add activity listeners
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Start idle check interval (every minute)
    idleTimerRef.current = setInterval(checkIdleTimeout, 60 * 1000);

    // Start session timeout check
    const startTime = sessionStorage.getItem('sessionStart');
    if (startTime) {
      const remaining = SESSION_CONFIG.MAX_SESSION - (Date.now() - parseInt(startTime, 10));
      if (remaining > 0) {
        sessionTimerRef.current = setTimeout(() => {
          forceLogout('max_session_exceeded');
        }, remaining);
      } else {
        forceLogout('max_session_exceeded');
      }
    }

    return () => {
      // Cleanup
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, [user, updateActivity, checkIdleTimeout, forceLogout]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      
      if (user) {
        // Check if session is still valid
        const start = sessionStorage.getItem('sessionStart');
        if (start) {
          const sessionDuration = Date.now() - parseInt(start, 10);
          if (sessionDuration >= SESSION_CONFIG.MAX_SESSION) {
            forceLogout('max_session_exceeded');
            return;
          }
        }
        
        // Update activity on auth state change
        lastActivityRef.current = Date.now();
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [forceLogout]);

  // Handle browser/tab close - logout if not "remember me"
  useEffect(() => {
    const handleBeforeUnload = () => {
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      if (!rememberMe && user) {
        // Can't async logout on beforeunload, but session persistence handles this
        // Just clear the session marker
        sessionStorage.removeItem('sessionStart');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user]);

  const login = async (email, password, rememberMe = false) => {
    try {
      // Set persistence based on remember me
      if (rememberMe) {
        await setPersistence(auth, browserLocalPersistence);
        localStorage.setItem('rememberMe', 'true');
        
        // Set expiry for remember me (7 days)
        const expiry = Date.now() + (SESSION_CONFIG.REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000);
        localStorage.setItem('rememberMeExpiry', expiry.toString());
      } else {
        await setPersistence(auth, browserSessionPersistence);
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberMeExpiry');
      }

      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Store session start time
      const now = Date.now();
      sessionStorage.setItem('sessionStart', now.toString());
      setSessionStart(now);
      lastActivityRef.current = now;
      
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Clear all session data
      sessionStorage.removeItem('sessionStart');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('rememberMeExpiry');
      
      // Clear timers
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Check remember me expiry on load
  useEffect(() => {
    const expiry = localStorage.getItem('rememberMeExpiry');
    if (expiry && Date.now() > parseInt(expiry, 10)) {
      // Remember me has expired
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('rememberMeExpiry');
      forceLogout('remember_me_expired');
    }
  }, [forceLogout]);

  // Method to update clocked-in status from TeamApp
  const updateClockedInStatus = useCallback((status) => {
    setIsClockedIn(status);
  }, []);

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
    isClockedIn,
    updateClockedInStatus,
    sessionStart
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
